<template>
  <div>
    <hr class="top-line" />
    <Form
      ref="reviewForm"
      v-slot="{ validate, isSubmitting, isValidating, errors: formErrors }"
      @invalid-submit="onInvalidSubmit"
      class="add-campsite-review"
    >
      <div
        class="overallrate"
        :class="{
          error: formErrors.overallRate && formErrors.overallRate.length > 0,
        }"
      >
        <label>
          <translate> Overall </translate><span class="asterisk">*</span>
        </label>
        <review-star-rating
          v-model="overallRate"
          name="overallRate"
          data-test="overallrate"
          data-allow-mismatch
          :validation-rules="overallRateValidationRules"
        >
          <template #errorMessage="{ errorMessage }">
            <translate
              v-if="errorMessage === 'required'"
              tag="p"
              class="input-error-msg"
            >
              Please rate your stay
            </translate>
          </template>
          <template #pointer>
            <div class="pointer-label">
              <div class="top-triangle" />
              <translate>Click to rate</translate>
            </div>
          </template>
          <template #helpText>
            <translate tag="p" class="help-text">
              From 1 (poor) to 10 (excellent)
            </translate>
          </template>
        </review-star-rating>
      </div>

      {{ trackFormErrors(formErrors, isSubmitting, isValidating) }}

      <pu-popover>
        <template #trigger>
          <div class="tips-popover-trigger">
            <img
              loading="lazy"
              src="@/assets/images/info.svg"
              width="21"
              height="21"
              alt=""
            />
            <translate>Tips for writing a great review</translate>
          </div>
        </template>
        <template #content>
          <translate tag="h3">Tips for writing a great review</translate>
          <ul class="tips-list">
            <li>
              <translate
                >Please keep any criticism fair, courteous and constructive - so
                that managers can act on it</translate
              >
            </li>
            <li>
              <translate
                >If you quote prices, remember that they normally vary during
                the season</translate
              >
            </li>
            <li>
              <i18n-t
                keypath="The site owner may have your booking details and may reply to you about this review. Do not use the review for complaints - complain to the campsite or (for complaints about the booking) {contactPitchupLink}"
              >
                <template #contactPitchupLink>
                  <translate
                    tag="a"
                    :href="`https://www.pitchup.com/${
                      $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
                    }about/contact/`"
                    target="_blank"
                  >
                    contact Pitchup.com
                  </translate>
                </template>
              </i18n-t>
            </li>
            <li>
              <translate
                >Avoid personal insults and naming of individuals</translate
              >
            </li>
            <li>
              <translate
                >Pitchup.com advises reviewers to keep photographic evidence if
                a review contains allegations which may be serious enough to
                prompt legal action</translate
              >
            </li>
            <li>
              <i18n-t
                keypath="Remember - {itsIllegalLink} to submit fake reviews, copy other people's content without permission or pay someone to submit content that benefits your business."
              >
                <template #itsIllegalLink>
                  <translate
                    tag="a"
                    href="http://www.out-law.com/page-12016"
                    target="_blank"
                  >
                    it's illegal
                  </translate>
                </template>
              </i18n-t>
              <translate
                >Reviews submitted by managers will not be posted</translate
              >
            </li>
            <li>
              <translate
                >Lastly, please avoid unnecessary CAPS, swear words, vulgar
                comments or sexually explicit language and keep an eye out for
                typos!</translate
              >
            </li>
          </ul>
        </template>
      </pu-popover>

      <fieldset v-if="shouldShowStayedInOptions">
        <base-select
          :default-select-name="defaultSelectName"
          :select-options="stayedInOptions"
          :required="true"
          :validation-rules="stayedInValidationRules"
          :data-sctrack="`${String($route.name)}--AddCampsiteReviewForm-stayedIn`"
          name="stayedIn"
          css-class="asblock"
          data-test="stayed-in"
          data-allow-mismatch
          @change="stayedIn = $event.selectedValue"
          tabindex="0"
        >
          <translate tag="option" value=""> Please choose... </translate>
          <option
            v-for="option in stayedInOptions"
            :key="option.value"
            :value="option.value"
            :selected="option.selected"
          >
            {{ option.name }}
          </option>
          <template #label>
            <translate>Stayed in</translate><span class="asterisk">*</span>
          </template>
          <template #errorMessage>
            <translate>Please tell us your accommodation type</translate>
          </template>
        </base-select>
      </fieldset>

      <base-textarea
        v-model="liked"
        :rows="6"
        :max-rows="30"
        :required="true"
        :validation-errors="errors.liked"
        :validation-rules="likedValidationRules"
        :max-length="4000"
        name="liked"
        css-class="asblock"
        :data-sctrack="`${String($route.name)}--AddCampsiteReviewForm-liked`"
        :placeholder="
          $t(
            'e.g. Spacious pitches with plenty of privacy, great walking trails right from the site and a lovely welcome from the owners',
          )
        "
        data-test="liked"
        data-allow-mismatch
        tabindex="0"
      >
        <template #label>
          <translate>Liked</translate><span class="asterisk">*</span>
        </template>
        <template #errorMessage="{ hasError }">
          <translate v-if="hasError('max_value')" :tt="{ max: 4000 }">
            Please shorten this text to {max} characters or fewer
          </translate>
          <translate v-else> Please tell us what you liked </translate>
        </template>
      </base-textarea>

      <base-textarea
        v-model="disliked"
        :rows="6"
        :max-rows="30"
        :validation-errors="errors.disliked"
        :validation-rules="dislikedValidationRules"
        :max-length="4000"
        css-class="asblock"
        :data-sctrack="`${String($route.name)}--AddCampsiteReviewForm-disliked`"
        :placeholder="
          $t(
            'e.g. Limited shower availability during peak times, pitches near the road can be noisy at night',
          )
        "
        name="disliked"
        data-test="disliked"
        data-allow-mismatch
        tabindex="0"
      >
        <template #label>
          <translate>Disliked</translate>
        </template>
        <template #errorMessage="{ hasError }">
          <translate v-if="hasError('max_value')" :tt="{ max: 4000 }">
            Please shorten this text to {max} characters or fewer
          </translate>
        </template>
      </base-textarea>

      <base-textarea
        v-model="summary"
        name="title"
        :rows="2"
        css-class="asblock"
        :data-sctrack="`${String($route.name)}--AddCampsiteReviewForm-title`"
        :validation-rules="summaryValidationRules"
        :placeholder="
          $t(
            'e.g. Excellent site with clean facilities, friendly staff and level pitches',
          )
        "
        data-test="summary"
        data-allow-mismatch
        tabindex="0"
      >
        <template #label>
          <translate>Summary</translate>
        </template>
        <template #errorMessage="{ hasError }">
          <translate v-if="hasError('max_value')" :tt="{ max: 70 }">
            Please shorten this text to {max} characters or fewer
          </translate>
        </template>
      </base-textarea>

      <fieldset>
        <base-select
          :default-select-name="defaultSelectName"
          :select-options="travellingAsOptions"
          name="travellingAs"
          :validation-rules="travellingAsValidationRules"
          :data-sctrack="`${String($route.name)}--AddCampsiteReviewForm-travellingAs`"
          css-class="asblock"
          data-test="traveling-as"
          data-allow-mismatch
          @change="travellingAs = $event.selectedValue"
        >
          <option
            v-for="option in travellingAsOptions"
            :key="option.value"
            :value="option.value"
            :selected="option.selected"
          >
            {{ option.name }}
          </option>
          <template #label>
            <translate>Travelling as</translate>
          </template>
        </base-select>
      </fieldset>

      <div>
        <base-checkbox v-model="anonymous" data-allow-mismatch tabindex="0">
          <translate>
            Make this review anonymous on our public website
          </translate>
        </base-checkbox>
      </div>
      <hr />

      <div class="ratings-section">
        <label
          ><translate> Location </translate>
          <translate class="label-help">(optional)</translate></label
        >
        <div class="rating-wrapper" :class="{ unrated: !rateLocation }">
          <review-star-rating
            v-model="rateLocation"
            name="location"
            data-test="location"
            data-allow-mismatch
          >
            <template #pointer>
              <div class="pointer-label">
                <div class="top-triangle" />
                <translate>Click to rate</translate>
              </div>
            </template>
          </review-star-rating>
        </div>

        <label
          ><translate> Value for money </translate>
          <translate class="label-help">(optional)</translate>
        </label>
        <review-star-rating
          v-model="rateValue"
          name="rateValue"
          data-test="rateValue"
          data-allow-mismatch
        />

        <template v-if="shouldShowQualityOfAccommodationOptions">
          <label
            ><translate> Quality of accommodation </translate>
            <translate class="label-help">(optional)</translate></label
          >
          <review-star-rating
            v-model="rateAccomodation"
            name="accomodation"
            data-test="accomodation"
            data-allow-mismatch
          />
        </template>

        <label>
          <translate> Facilities / Activities </translate>
          <translate class="label-help">(optional)</translate></label
        >
        <review-star-rating
          v-model="rateFacilities"
          name="facilities"
          data-test="facilities"
          data-allow-mismatch
        />

        <label>
          <translate> Customer service </translate>
          <translate class="label-help">(optional)</translate></label
        >
        <review-star-rating
          v-model="rateService"
          name="service"
          data-test="service"
          data-allow-mismatch
        />

        <template v-if="shouldShowChildrenOptions">
          <label>
            <translate> Child friendliness </translate>
            <translate class="label-help">(optional)</translate></label
          >
          <review-star-rating
            v-model="rateChildFriendly"
            name="childFriendly"
            data-test="childFriendly"
            data-allow-mismatch
          />
        </template>

        <label>
          <translate> Cleanliness </translate>
          <translate class="label-help">(optional)</translate></label
        >
        <review-star-rating
          v-model="rateClean"
          name="clean"
          data-test="clean"
          data-allow-mismatch
        />

        <label>
          <translate> Wifi </translate>
          <translate class="label-help">(optional)</translate></label
        >
        <review-star-rating
          v-model="rateWifi"
          name="wifi"
          data-test="wifi"
          data-allow-mismatch
        />
      </div>

      <hr />

      <pu-accordion
        class="optional-section"
        data-test="themesOptions"
        :selected="shouldShowThemesOption"
        :accordion-id="`accordion-themes-${campsiteSlug}`"
        @accordion-open="openThemesOption"
        @accordion-close="closeThemesOption"
      >
        <template #title>
          <translate
            tag="label"
            :tt="{ campsiteName }"
            class="field-label"
            tabindex="0"
          >
            Would you describe {campsiteName} as...?
          </translate>
          <translate class="help-text"> (Optional section) </translate>
        </template>
        <template #body>
          <base-checkbox
            v-for="theme in themesOptions"
            :key="theme.value"
            v-model="theme.selected"
            name="theme"
            :val="theme.value"
            :data-test="`theme-${theme.value}`"
            data-allow-mismatch
          >
            {{ theme.name }}
          </base-checkbox>
        </template>
      </pu-accordion>

      <hr />

      <label class="field-label yes-no">
        <translate> Would you recommend this to a friend? </translate
        ><span class="asterisk">*</span>
      </label>
      <base-radio-options
        v-model="recommendToAFriend"
        :validation-rules="recommendToAFriendValidationRules"
        :options="[
          { value: 'yes', name: $t('Yes') },
          { value: 'no', name: $t('No') },
        ]"
        name="recf"
        data-allow-mismatch
      >
        <template #errorMessage="{ failedRules }">
          <translate
            v-if="failedRules === 'required'"
            tag="p"
            class="input-error-msg"
          >
            Please choose Yes or No
          </translate>
        </template>
      </base-radio-options>

      <label class="field-label yes-no">
        <translate> Would you stay here again? </translate
        ><span class="asterisk">*</span>
      </label>
      <base-radio-options
        v-model="willYouReturn"
        :validation-rules="willYouReturnValidationRules"
        :options="[
          { value: 'yes', name: $t('Yes') },
          { value: 'no', name: $t('No') },
        ]"
        name="rec"
        data-allow-mismatch
      >
        <template #errorMessage="{ failedRules }">
          <translate
            v-if="failedRules === 'required'"
            tag="p"
            class="input-error-msg"
          >
            Please choose Yes or No
          </translate>
        </template>
      </base-radio-options>

      <hr />

      <translate tag="label" class="field-label">
        Show off your photography skills (optional)
      </translate>
      <translate tag="p">
        Photos will appear alongside your review and in the main site gallery.
      </translate>

      <client-only>
        <campsite-image-uploader
          :campsite-slug="campsiteSlug"
          :token="token"
          @input="photos = $event"
          @photos-valid="photosValid = $event"
          @caption-blur="captionBlur"
        />
      </client-only>

      <hr />

      <pu-accordion
        class="optional-section private-feedback"
        data-test="privateFeedback"
        :selected="shouldShowPrivateFeedbackOption"
        :accordion-id="`private-feedback-${campsiteSlug}`"
        @accordion-open="openPrivateFeedbackOption"
        @accordion-close="closePrivateFeedbackOption"
      >
        <template #title>
          <translate tag="label" class="field-label" tabindex="0">
            Private feedback for the site owner
          </translate>
          <translate class="help-text"> (Optional section) </translate>
        </template>
        <template #body>
          <base-textarea
            v-model="privateMessage"
            :rows="6"
            :max-rows="30"
            :validation-errors="errors.privateMessage"
            :validation-rules="privateMessageValidationRules"
            :max-length="4000"
            :placeholder="
              $t(
                'e.g. The tap near pitch 12 was dripping throughout our stay - worth getting fixed before next season',
              )
            "
            css-class="asblock"
            :data-sctrack="`${String($route.name)}--AddCampsiteReviewForm-privateMessage`"
            name="private-feedback"
            data-allow-mismatch
            tabindex="0"
          >
            <template #label></template>
            <template #label-status> &#8203; </template>
            <template #helpText>
              <translate>
                This will not be published as part of your review.
              </translate>
            </template>
            <template #errorMessage="{ hasError }">
              <translate v-if="hasError('max_value')" :tt="{ max: 4000 }">
                Please shorten this text to {max} characters or fewer
              </translate>
            </template>
          </base-textarea>
        </template>
      </pu-accordion>

      <hr />

      <translate tag="p" class="wrapper reviews-footnote">
        Please check your review carefully - unfortunately you won't be able to
        edit it afterwards.
      </translate>

      <div class="button-wrapper">
        <base-submit-button
          class="primary asblock"
          data-test="submit"
          :disabled="!booking"
          :submitting="states.includes('submitting')"
          @click="submit(validate)"
          tabindex="0"
        >
          <translate>Add your review</translate>
          <template #submittingText>
            <translate>Saving...</translate>
          </template>
        </base-submit-button>
        <span v-if="hasSubmitError"
          >Cannot submit review because of an error</span
        >
      </div>
    </Form>
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
import { Field, Form } from 'vee-validate'
import * as z from 'zod'
import { AddReviewStateMachineInterpreter } from '~/apps/post-review/machine'
import { ReviewFormData } from '~/apps/post-review/types'
import { Component, Prop, toNative, Vue } from '~/utility/pu-class-decorator'
import { useFormStore } from '~/stores/useFormStore'
import { isEqual } from 'lodash-es'

@Component({
  components: { Form, Field },
})
class AddCampsiteReviewForm extends Vue {
  @Prop()
  campsiteName: string

  @Prop()
  campsiteSlug: string

  @Prop()
  token: string

  @Prop()
  booking: AddReviewStateMachineInterpreter['state']['context']['booking']

  @Prop()
  errors: Record<string, string[]>

  @Prop()
  states: string[]

  @Prop()
  initialOverallRating?: number

  anonymous = false
  disliked = ''
  liked = ''
  privateMessage = ''
  overallRate = 0
  rateAccomodation = 0
  rateChildFriendly = 0
  rateClean = 0
  rateFacilities = 0
  rateLocation = 0
  rateService = 0
  rateValue = 0
  rateWifi = 0
  recommendToAFriend: 'yes' | 'no' | null = null
  stayedIn = ''
  summary = ''
  travellingAs = '0'
  willYouReturn: 'yes' | 'no' | null = null
  photos: string[] = []

  photosValid = false

  formStore: ReturnType<typeof useFormStore> | undefined = undefined

  get defaultSelectName() {
    return this.$t('Please choose...')
  }

  overallRateErrors: string[] = []

  shouldShowThemesOption = false
  shouldShowPrivateFeedbackOption = false

  overallRateValidationRules = z
    .number({ required_error: 'required' })
    .min(1, { message: 'required' })

  willYouReturnValidationRules = z
    .string({ required_error: 'required' })
    .min(1, { message: 'required' })

  recommendToAFriendValidationRules = z
    .string({ required_error: 'required' })
    .min(1, { message: 'required' })

  get stayedInValidationRules() {
    // If booking has unitType, field is optional; otherwise required
    if (this.booking?.unitType) {
      return z.string().optional().nullable()
    }
    return z
      .string({ required_error: 'required' })
      .min(1, { message: 'required' })
  }

  likedValidationRules = z
    .string({ required_error: 'required' })
    .max(4000, { message: 'max_value' })
    .min(1, { message: 'required' })
    .refine((v) => v.trim().length > 0, { message: 'required' })

  dislikedValidationRules = z.string().max(4000, { message: 'max_value' })

  privateMessageValidationRules = z.string().max(4000, { message: 'max_value' })

  summaryValidationRules = z.string().max(70, { message: 'max_value' })

  travellingAsValidationRules = z.string().optional()

  hasSubmitError: boolean = false
  isSubmitting = false

  async mounted() {
    // Initialize form store and restore data immediately for better UX
    if (process.client) {
      this.formStore = useFormStore(usePinia())

      // Wait for next tick to ensure all child components are mounted
      await this.$nextTick()

      // Pre-fill overall rating from email link if provided.
      // restoreFormData() runs after this and will override with any saved localStorage value,
      // so the user's in-progress form data always takes priority.
      if (this.initialOverallRating !== undefined) {
        this.overallRate = this.initialOverallRating
        const form = this.$refs.reviewForm as any
        if (form?.setFieldValue) {
          form.setFieldValue('overallRate', this.initialOverallRating)
        }
      }

      // Restore form data immediately - no delay needed with data-allow-mismatch attributes
      await this.restoreFormData()

      // Set up watchers after data is restored
      this.setupFieldWatchers()
    }
  }

  // Rating fields configuration to eliminate repetition
  private ratingFields = [
    { prop: 'overallRate', field: 'overallRate' },
    { prop: 'rateLocation', field: 'location' },
    { prop: 'rateValue', field: 'rateValue' },
    { prop: 'rateAccomodation', field: 'rateAccomodation' },
    { prop: 'rateFacilities', field: 'facilities' },
    { prop: 'rateService', field: 'service' },
    { prop: 'rateChildFriendly', field: 'childFriendly' },
    { prop: 'rateClean', field: 'clean' },
    { prop: 'rateWifi', field: 'wifi' },
  ] as const

  async restoreFormData() {
    if (!this.formStore || !this.formStore.formData.review) return

    const reviewData = this.formStore.formData.review as Record<string, any>

    // Get the Form ref to update vee-validate state
    const form = this.$refs.reviewForm as any

    // Restore all form fields from the saved review object
    // Text fields
    if (reviewData.anonymous !== undefined)
      this.anonymous = reviewData.anonymous
    if (reviewData.disliked !== undefined) {
      this.disliked = reviewData.disliked
      if (form && form.setFieldValue) {
        form.setFieldValue('disliked', this.disliked)
      }
    }
    if (reviewData.liked !== undefined) {
      this.liked = reviewData.liked
      if (form && form.setFieldValue) {
        form.setFieldValue('liked', this.liked)
      }
    }
    if (reviewData.privateMessage !== undefined) {
      this.privateMessage = reviewData.privateMessage
      if (form && form.setFieldValue) {
        form.setFieldValue('privateMessage', this.privateMessage)
      }
    }
    if (reviewData.summary !== undefined) {
      this.summary = reviewData.summary
      if (form && form.setFieldValue) {
        form.setFieldValue('summary', this.summary)
      }
    }

    // Radio buttons - standardized to always use strings ('yes'/'no')
    if (
      reviewData.recommendToAFriend !== undefined &&
      reviewData.recommendToAFriend !== null
    ) {
      this.recommendToAFriend = String(reviewData.recommendToAFriend) as
        | 'yes'
        | 'no'
      // Update vee-validate state - use the correct field name 'recf'
      if (form && form.setFieldValue) {
        form.setFieldValue('recf', this.recommendToAFriend)
      }
    }
    if (
      reviewData.willYouReturn !== undefined &&
      reviewData.willYouReturn !== null
    ) {
      this.willYouReturn = String(reviewData.willYouReturn) as 'yes' | 'no'
      // Update vee-validate state - use the correct field name 'rec'
      if (form && form.setFieldValue) {
        form.setFieldValue('rec', this.willYouReturn)
      }
    }

    // Dropdowns
    if (
      reviewData.stayedIn !== undefined &&
      reviewData.stayedIn !== null &&
      reviewData.stayedIn !== ''
    ) {
      this.stayedIn = String(reviewData.stayedIn)
      if (form && form.setFieldValue) {
        form.setFieldValue('stayedIn', this.stayedIn)
      }
      // Trust Vue's reactive system to handle the update
      // No need for DOM polling - the v-model binding will handle this
    }
    if (reviewData.travellingAs !== undefined) {
      this.travellingAs = String(reviewData.travellingAs)
      if (form && form.setFieldValue) {
        form.setFieldValue('travellingAs', this.travellingAs)
      }
      // Trust Vue's reactive system to handle the update
      // No need for DOM manipulation - the v-model binding will handle this
    }

    // Accordion states
    if (reviewData.shouldShowThemesOption !== undefined)
      this.shouldShowThemesOption = reviewData.shouldShowThemesOption
    if (reviewData.shouldShowPrivateFeedbackOption !== undefined)
      this.shouldShowPrivateFeedbackOption =
        reviewData.shouldShowPrivateFeedbackOption

    // Theme checkboxes
    if (reviewData.themesOptions !== undefined) {
      const savedThemes = reviewData.themesOptions as string[]
      this.themesOptions.forEach((theme) => {
        theme.selected = savedThemes.includes(theme.value)
      })
    }

    // Set star rating values from stored data using loop to reduce repetition
    for (const { prop, field } of this.ratingFields) {
      if (prop in reviewData) {
        const value = Number(reviewData[prop])
        const numericValue = isNaN(value) ? 0 : value
        this[prop] = numericValue
        // Update vee-validate state
        if (form && form.setFieldValue) {
          form.setFieldValue(field, numericValue)
        }
      }
    }

    // Wait for Vue to update
    await this.$nextTick()

    // All star ratings have been restored from localStorage

    // Clear any previous validation errors since we're restoring valid data
    if (form && form.setErrors) {
      form.setErrors({})
    }

    // Force update all components
    this.$forceUpdate()

    // Validate the form to ensure all restored fields are properly recognized
    // Use form.validate() instead of manual event dispatching
    if (form && form.validate) {
      form.validate()
    }
  }

  setupFieldWatchers() {
    // Create a computed property for the review data
    const getReviewData = () => ({
      anonymous: this.anonymous,
      disliked: this.disliked,
      liked: this.liked,
      privateMessage: this.privateMessage,
      overallRate: this.overallRate,
      rateAccomodation: this.rateAccomodation,
      rateChildFriendly: this.rateChildFriendly,
      rateClean: this.rateClean,
      rateFacilities: this.rateFacilities,
      rateLocation: this.rateLocation,
      rateService: this.rateService,
      rateValue: this.rateValue,
      rateWifi: this.rateWifi,
      recommendToAFriend: this.recommendToAFriend, // Save as 'yes'/'no' string, not boolean
      stayedIn: this.stayedIn,
      summary: this.summary,
      travellingAs: this.travellingAs,
      willYouReturn: this.willYouReturn, // Save as 'yes'/'no' string, not boolean
      shouldShowThemesOption: this.shouldShowThemesOption,
      shouldShowPrivateFeedbackOption: this.shouldShowPrivateFeedbackOption,
      themesOptions: this.themesOptions
        .filter((t) => t.selected)
        .map((t) => t.value),
    })

    // Watch all form fields and update the entire review object when any field changes
    this.$watch(
      () => getReviewData(),
      (newData) => {
        this.formStore?.updateField('review', newData)
      },
      { deep: true },
    )
  }

  private lastTrackedErrors: Record<string, string> = {}
  private previousErrors: Record<string, string> = {}

  trackFormErrors(errors, isSubmitting: boolean, isValidating: boolean) {
    try {
      // Because we are running this function on every render we need to avoid firing events
      // when the form is submitting or validating. We also need to avoid firing
      // the GTM events if they are the same as last time this function was run
      // to avoid errors being set when submitting the form and then getting sent
      // the next time a render is performed when the form are no longer submitting but
      // have submission errors showing.
      const sameErrors = isEqual(this.previousErrors, errors)
      this.previousErrors = errors

      if (isSubmitting || isValidating || sameErrors) {
        return
      }

      // Compare new errors with previously tracked ones
      Object.keys(errors).forEach((fieldName) => {
        const currentError = errors[fieldName]
        const trackedError = this.lastTrackedErrors[fieldName]

        // Only fire GTM for NEW errors (not previously tracked)
        if (currentError && currentError !== trackedError) {
          const gtmEvent = {
            event: 'review_form_error',
            field: fieldName.includes('caption') ? 'caption' : fieldName,
            message: errors[fieldName],
            trigger: 'blur',
          }

          this.$gtm.push(gtmEvent)
        }

        this.lastTrackedErrors[fieldName] = errors[fieldName]
      })
    } catch (e) {
      console.error('Error in trackFormErrors:', e)
    }

    return ''
  }

  onInvalidSubmit({ errors }) {
    Object.keys(errors).forEach((key) => {
      const gtmEvent = {
        event: 'review_form_error',
        field: key.includes('caption') ? 'caption' : key,
        message: errors[key],
        trigger: 'submit',
      }
      this.$gtm.push(gtmEvent)
    })
  }

  submit(validate) {
    this.isSubmitting = true
    this.hasSubmitError = false
    this.doSubmit(validate).catch((error) => {
      this.hasSubmitError = true
      this.isSubmitting = false
      const err = error as Error
      err.name = 'add-campsite-review-form.submit error'
      throw err
    })
  }

  private async doSubmit(validate) {
    const valid = await validate()
    if (!valid.valid) {
      const errorElement = document.querySelector('.error')
      if (!errorElement) {
        console.error(
          'No error element found',
          `valid=${valid.valid}`,
          JSON.stringify(this.overallRateErrors),
          `photosValid=${this.photosValid}`,
        )
      }
      this.$puScrollTo('.error', {
        duration: 300,
        offset: -20,
      })
      this.isSubmitting = false
    } else {
      this.$emit('submit', this.formData)
      setTimeout(() => {
        if (document.querySelector('.error')) {
          this.$puScrollTo('.error', {
            duration: 300,
            offset: -20,
          })
        }
      }, 1000)
    }
  }

  // visitDate comes from booking.arrive
  private get formData(): Omit<ReviewFormData, 'visitDate'> {
    return {
      anonymous: this.anonymous,
      disliked: this.disliked,
      privateMessage: this.privateMessage,
      liked: this.liked,
      overallRate: this.overallRate,
      rateAccomodation: this.rateAccomodation,
      rateChildFriendly: this.rateChildFriendly,
      rateClean: this.rateClean,
      rateFacilities: this.rateFacilities,
      rateLocation: this.rateLocation,
      rateService: this.rateService,
      rateValue: this.rateValue,
      rateWifi: this.rateWifi,
      recommendToAFriend: this.recommendToAFriend === 'yes',
      stayedIn: this.selectedStayedInOptions,
      summary: this.summary,
      travellingAs: this.travellingAs,
      willYouReturn: this.willYouReturn === 'yes',
      theme: this.themesOptions
        .filter((o) => o.selected === true)
        .map((t) => t.value),
    }
  }

  openThemesOption() {
    this.shouldShowThemesOption = true
  }

  closeThemesOption() {
    this.shouldShowThemesOption = false
  }

  openPrivateFeedbackOption() {
    this.shouldShowPrivateFeedbackOption = true
  }

  closePrivateFeedbackOption() {
    this.shouldShowPrivateFeedbackOption = false
  }

  get shouldShowStayedInOptions() {
    if (!this.booking) return false
    if (this.booking?.unitType) return false
    return this.stayedInOptions.length > 1
  }

  get shouldShowChildrenOptions() {
    if (!this.booking) return false
    // If the booking has no party detailed, we need to show the children options
    else if (!this.booking.partyDetailed) return true
    // If the booking has party detailed, we need to show the children options if there are children or infants
    else
      return (
        this.booking?.partyDetailed?.children > 0 ||
        this.booking?.partyDetailed?.infants > 0
      )
  }

  get shouldShowQualityOfAccommodationOptions() {
    if (!this.booking) return false
    // If the booking is BYO, we don't need to show the quality of accommodation options
    return this.booking?.isBYO === false
  }

  get selectedStayedInOptions() {
    return this.stayedInOptions.length === 1
      ? this.stayedInOptions[0].value
      : this.stayedIn
  }

  get stayedInOptions() {
    const categories = this.booking?.categories || undefined
    if (categories) {
      return this.stayedInAllOptions.filter((o) => categories.includes(o.value))
    }
    return this.stayedInAllOptions
  }

  get travellingAsOptions() {
    const options = [
      { name: this.$t('Extended family'), value: '8', selected: false },
      {
        name: this.$t('Family with older children'),
        value: '2',
        selected: false,
      },
      {
        name: this.$t('Family with young children'),
        value: '1',
        selected: false,
      },
      { name: this.$t('Group-mostly female'), value: '10', selected: false },
      { name: this.$t('Group-mostly male'), value: '9', selected: false },
      {
        name: this.$t('Individual/group with accessibility needs'),
        value: '12',
        selected: false,
      },
      { name: this.$t('Mature couple'), value: '3', selected: false },
      { name: this.$t('Middle-aged couple'), value: '11', selected: false },
      { name: this.$t('Solo traveller-female'), value: '6', selected: false },
      { name: this.$t('Solo traveller-male'), value: '5', selected: false },
      { name: this.$t('With friends'), value: '7', selected: false },
      { name: this.$t('Young couple'), value: '4', selected: false },
    ]

    const sortedOptions = options.sort((a, b) => {
      return a.name.localeCompare(b.name)
    })
    return [
      { name: this.$t('Please choose...'), value: '0', selected: true }, // default - empty value
      ...sortedOptions,
    ]
  }

  get stayedInAllOptions() {
    const options = [
      { name: this.$t('Caravans for hire'), value: '6', selected: false },
      {
        name: this.$t('Lodges, cabins, pods or huts'),
        value: '7',
        selected: false,
      },
      { name: this.$t('Motorhome pitches'), value: '10', selected: false },
      { name: this.$t('Rent a tent'), value: '13', selected: false },
      { name: this.$t('Tent pitches'), value: '4', selected: false },
      { name: this.$t('Touring pitches'), value: '3', selected: false },
    ]

    return options.sort((a, b) => {
      return a.name.localeCompare(b.name)
    })
  }

  get themesOptions() {
    const options = [
      { name: this.$t('Forest'), value: '104', selected: false },
      { name: this.$t('Budget/backpacker'), value: '119', selected: false },
      { name: this.$t('Luxury'), value: '118', selected: false },
      { name: this.$t('Wild camping'), value: '45', selected: false },
      { name: this.$t('Lake and/or mountain'), value: '78', selected: false },
      { name: this.$t('Remote location'), value: '79', selected: false },
      { name: this.$t('Walkers paradise'), value: '84', selected: false },
      { name: this.$t('Peaceful'), value: '81', selected: false },
      { name: this.$t('Wildlife haven'), value: '80', selected: false },
      { name: this.$t('Spectacular scenery'), value: '77', selected: false },
      { name: this.$t('Leave the car at home'), value: '82', selected: false },
      { name: this.$t('Gastronomic delight'), value: '85', selected: false },
    ]
    return options.sort((a, b) => {
      return a.name.localeCompare(b.name)
    })
  }
}

export default toNative(AddCampsiteReviewForm)
</script>

<style scoped>
form {
  padding: 0 1.4em 1em;
}

legend {
  font-weight: normal;
}

hr {
  margin: 1em 0;
}

.top-line {
  background-color: var(--colour-page-background-light);
}

p {
  font-size: var(--body);
}

form :deep(.select select) {
  font-size: 1.24em;
}

form :deep(::-webkit-input-placeholder) {
  color: var(--colour-text-light);
}
form :deep(::-moz-placeholder) {
  color: var(--colour-text-light);
}
form :deep(:-ms-input-placeholder) {
  color: var(--colour-text-light);
}

form :deep(.label-status) {
  display: none;
}

form :deep(.base-radio-options) {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  padding: 0.5em 0 1em;
}

form :deep(.base-radio-options label) {
  display: flex;
  justify-content: center;
  margin: 0;
}

form :deep(.base-radio-options label ~ label) {
  margin: 0 0 0 3em;
}

form :deep(.input-error-msg) {
  font-size: var(--body);
  width: 100%;
  padding: 0.5em 1em;
}

form :deep(.base-radio-options + .input-error-msg) {
  margin-top: -0.5em;
}

.overallrate {
  padding: 0.8em 1em;
  box-shadow: var(--card-box-shadow);
  border-radius: var(--border-radius);
  margin: 1em -1em 2em;
}

.rating-wrapper {
  display: block;
}

.ratings-section label:not(:first-of-type) {
  padding-top: 1.5em;
  display: block;
}

.pointer-label {
  opacity: 0;
  transition: all 300ms ease-in-out 600ms;
  transition-behavior: allow-discrete;
  display: none;
}

.unrated .pointer-label {
  opacity: 1;
  display: table;
}

.pointer-label span {
  background: var(--colour-header-light-blue);
  min-height: 1.6em;
  height: auto;
  border-radius: 0.2em;
  position: relative;
  display: block;
  padding: 0.25em 0.5em;
  color: #fff;
  font-style: normal;
  line-height: 1.1;
}

.top-triangle {
  margin: 0 0 -1px 0.25em;
}

.top-triangle:after {
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid var(--colour-header-light-blue);
  content: '';
  display: block;
}

form :deep(.accordion) {
  border: none;
}

form :deep(.accordion-header label) {
  cursor: pointer;
}

form :deep(.accordion-content) {
  border-top: none;
}

form :deep(.private-feedback .accordion-content) {
  padding-top: 0;
}

form :deep(.accordion-content .base-checkbox) {
  padding-left: 1em;
}

form :deep(.accordion-content label) {
  margin-top: 0;
}

form :deep(.accordion-content textarea) {
  min-height: 130px;
}
.yes-no {
  margin-top: 0.5em;
  display: block;
}

/* tips popover */
form :deep(.pu-popover) {
  display: block;
}
form :deep(.info-button) {
  display: flex;
  align-items: center;
  justify-content: end;
  margin: 0 0 0 auto;
  font-size: 14.25px;
}
.tips-list {
  margin-top: 1.25em;
}
.tips-list li {
  font-size: var(--body);
  list-style: disc;
  margin-left: 1.5em;
  padding-bottom: 0.5em;
}
.tips-popover-trigger {
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 0.35em;
}
@media (hover: hover) {
  .tips-popover-trigger:hover {
    text-decoration: underline;
    & img {
      opacity: 1;
    }
  }
}
</style>
